/**
 * bossMissileBarrage.ts
 * Homing-missile pool + launch helper for the Void Mothership.
 *
 * Sibling of `bossSystem.ts` — extracted to keep that module within the
 * 300-line budget and to mirror the existing `bossTurret.ts` /
 * `bossDroneSpawner.ts` / `bossRam.ts` split.
 *
 * Owns:
 *  - the `BossMissile` pool (capped at
 *    `VOID_MOTHERSHIP_MAX_BOSS_MISSILES`, pre-warmed at module load),
 *  - `fireBossMissile()` — single-launch acquire/spawn,
 *  - `updateBossMissiles()` — per-tick integrate + cull,
 *  - `releaseAllBossMissiles()` — bulk release on scene teardown.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * @module bossMissileBarrage
 */

import { spaceState } from './spaceState';
import { spawnExplosion } from './explosionVfx';
import type { LevelRuntime } from './gameState';
import { BossMissile } from '../entities/BossMissile';
import { ObjectPool } from '../utils/objectPool';
import {
  VOID_MOTHERSHIP_MISSILE_WIDTH,
  VOID_MOTHERSHIP_MISSILE_HEIGHT,
  VOID_MOTHERSHIP_MAX_BOSS_MISSILES,
  VOID_MOTHERSHIP_MISSILE_TRAIL_INTERVAL_S,
} from '../../constants/bossConstants';

/** Reusable empty level for missile update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Module-level boss-missile pool. Pre-warmed at module load so the
 * first barrage launch doesn't pay an allocation cost. Sized to
 * `VOID_MOTHERSHIP_MAX_BOSS_MISSILES` — a finite ceiling above the peak
 * launch cadence × TTL, so `acquire()` returning `null` is a sign the
 * cadence/TTL was tuned too aggressively rather than a normal event.
 */
const bossMissilePool = new ObjectPool<BossMissile>(
  () => new BossMissile(),
  VOID_MOTHERSHIP_MAX_BOSS_MISSILES,
);
bossMissilePool.prewarm(VOID_MOTHERSHIP_MAX_BOSS_MISSILES);

/**
 * Test-only helper: number of boss missiles currently parked in the
 * pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _bossMissilePoolAvailable(): number {
  return bossMissilePool.available;
}

/**
 * Spawn a single boss-fired homing missile at the boss's centre-left
 * edge. Returns `true` when a missile was launched, `false` when the
 * pool was exhausted. Callers use the return value to gate the missile
 * cooldown so a pool miss doesn't burn the shot (pool-miss-no-burn —
 * see the matching DECISION in `tryFireShipProjectile`).
 *
 * @returns `true` on launch, `false` on a pool miss or absent boss.
 */
export function fireBossMissile(): boolean {
  const boss = spaceState.boss;
  if (boss === null) return false;
  const missile = bossMissilePool.acquire();
  if (missile === null) return false;
  // Spawn so the missile's RIGHT edge is flush with the boss's LEFT
  // edge — mirrors the turret-bolt muzzle offset so the two weapons
  // appear to emerge from the same forward face.
  const spawnX = boss.x - VOID_MOTHERSHIP_MISSILE_WIDTH;
  const spawnY = boss.y + boss.height / 2 - VOID_MOTHERSHIP_MISSILE_HEIGHT / 2;
  missile.spawn(spawnX, spawnY);
  spaceState.bossMissiles.push(missile);
  return true;
}

/**
 * Update every active boss missile and release dead ones back to the
 * pool. Mirrors `updateBossProjectiles` — runs after `updateBoss` so a
 * missile launched on this tick gets its first dt of motion on the
 * same frame, and keeps culling even after the boss dies so already-
 * launched missiles don't visually freeze.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateBossMissiles(dt: number): void {
  const arr = spaceState.bossMissiles;
  for (let i = arr.length - 1; i >= 0; i--) {
    const m = arr[i];
    m.update(dt, EMPTY_SPACE_LEVEL);
    if (m.dead) {
      bossMissilePool.release(m);
      arr.splice(i, 1);
      continue;
    }
    // Trail VFX: spawn a burst at the missile's centre on a fixed
    // cadence. Accumulator pattern (mirrors `bossRam` trail) keeps the
    // spacing dt-independent across a long hitch. Iteration count is
    // bounded by `ceil(dt / VOID_MOTHERSHIP_MISSILE_TRAIL_INTERVAL_S)`
    // — fixed-step physics means dt is the same every tick, so this
    // is effectively a constant per call. `spawnExplosion` is a
    // silent no-op on pool saturation (AGENTS.md pool rule), so an
    // over-budget spawn doesn't crash but does silently drop the VFX.
    m.trailTimer -= dt;
    while (m.trailTimer < 0) {
      spawnExplosion(m.x + m.width / 2, m.y + m.height / 2);
      m.trailTimer += VOID_MOTHERSHIP_MISSILE_TRAIL_INTERVAL_S;
    }
  }
}

/**
 * Release every live boss missile back to the pool and clear the
 * scene's bookkeeping list. Called from `resetBoss()` so a re-init of
 * the space level starts from a clean slate.
 */
export function releaseAllBossMissiles(): void {
  for (let i = 0; i < spaceState.bossMissiles.length; i++) {
    bossMissilePool.release(spaceState.bossMissiles[i]);
  }
  spaceState.bossMissiles.length = 0;
}
